/**
 * systemAgentContacts.ts
 * 将37名系统数字员工转换为通讯录联系人格式
 * 供官方工作台的 ChatSidebar 使用
 */

import { systemAgents, systemDepts } from './systemAgents';

/** 系统员工通讯录联系人类型 */
export interface SystemAgentContact {
  id: string;
  name: string;
  role: string;
  avatar: string;
  status: 'online' | 'busy' | 'offline';
  lastMessage: string;
  lastTime: string;
  lastActiveAt: number;
  unread: number;
  department: string;
  deptId: string;
  deptName: string;
  deptColor: string;
  deptIcon: string;
  type: 'system_agent';
  isSystemAgent: true;
  agentCode: string;
  agentLevel: number;
  recentAction: string;
}

/** 真实员工账号列表（用于 badge 标识） */
export const REAL_STAFF_NAMES = ['杨先生', '石先生', '李先生', '内部员工01', '内部员工02'];
export const REAL_STAFF_ACCOUNTS = ['yang', 'shi', 'li', 'staff01', 'staff02'];

/** 将系统员工状态映射为通讯录状态 */
function mapStatus(status: string): 'online' | 'busy' | 'offline' {
  if (status === 'running' || status === 'executing') return 'online';
  if (status === 'waiting') return 'busy';
  return 'offline';
}

/** 生成系统员工通讯录联系人列表 */
export const systemAgentContacts: SystemAgentContact[] = systemAgents.map(agent => {
  const dept = systemDepts.find(d => d.id === agent.deptId);
  return {
    id: `sys-contact-${agent.id}`,
    name: agent.name,
    role: agent.title,
    avatar: agent.avatar,
    status: mapStatus(agent.status),
    lastMessage: agent.recentAction ?? '系统数字员工，随时待命',
    lastTime: agent.lastRun ? agent.lastRun.slice(11, 16) : '—',
    lastActiveAt: agent.lastRun
      ? new Date(agent.lastRun).getTime()
      : Date.now() - 1000 * 60 * 30,
    unread: 0,
    department: dept?.name ?? '系统部门',
    deptId: agent.deptId,
    deptName: dept?.name ?? '系统部门',
    deptColor: dept?.color ?? '#64748b',
    deptIcon: dept?.icon ?? 'ri-robot-2-line',
    type: 'system_agent' as const,
    isSystemAgent: true as const,
    agentCode: agent.code,
    agentLevel: agent.level,
    recentAction: agent.recentAction ?? '',
  };
});

/** 按部门分组的系统员工 */
export interface SystemAgentDeptGroup {
  deptId: string;
  deptName: string;
  deptColor: string;
  deptIcon: string;
  agents: SystemAgentContact[];
}

export const systemAgentsByDept: SystemAgentDeptGroup[] = systemDepts.map(dept => ({
  deptId: dept.id,
  deptName: dept.name,
  deptColor: dept.color,
  deptIcon: dept.icon,
  agents: systemAgentContacts.filter(a => a.deptId === dept.id),
})).filter(g => g.agents.length > 0);
